import React, { useState, useEffect, useRef, useImperativeHandle, forwardRef } from 'react';
import { Search, Filter, X } from 'lucide-react';
import { getConfig } from '../config';
import './SearchForm.css';

const SearchForm = forwardRef(({ onSearch, initialParams }, ref) => {
  const config = getConfig();
  const submitButtonRef = useRef(null);
  const formRef = useRef(null);

  useImperativeHandle(ref, () => ({
    triggerSearch: () => {
      if (submitButtonRef.current) {
        submitButtonRef.current.click();
      }
    }
  }));
  
  // Функция для чтения параметров из URL
  // Возвращает null если параметр отсутствует, иначе строку (может быть пустой)
  const getUrlParams = () => {
    const urlParams = new URLSearchParams(window.location.search);
    return {
      author: urlParams.has('author') ? urlParams.get('author') || '' : null,
      content_search: urlParams.has('content_search') ? urlParams.get('content_search') || '' : null
    };
  };

  // Инициализируем форму значениями из URL или initialParams
  const [formData, setFormData] = useState(() => {
    const urlParams = getUrlParams();
    return {
      // Если параметр есть в URL - используем его, иначе initialParams, иначе очищаем
      author: urlParams.author !== null ? urlParams.author : (initialParams?.author || ''),
      content_search: urlParams.content_search !== null ? urlParams.content_search : (initialParams?.content_search || ''),
      datetime: {
        operator: 'gte',
        value: ''
      },
      rating_plus: {
        operator: 'gte',
        value: ''
      },
      rating_minus: {
        operator: 'gte',
        value: ''
      },
      haveimages: false,
      havevideos: false,
      limit: initialParams?.limit || config.app.defaultLimit,
      offset: initialParams?.offset || 0
    };
  });

  // Обновляем форму при изменении URL или initialParams
  useEffect(() => {
    const urlParams = getUrlParams();
    
    // Всегда обновляем форму на основе URL
    // Если параметр есть в URL - используем его (может быть пустой строкой)
    // Если параметра нет в URL - очищаем поле
    setFormData(prev => ({
      ...prev,
      // Используем значение из URL если оно есть, иначе очищаем
      // initialParams используется только как fallback при первой загрузке
      author: urlParams.author !== null ? urlParams.author : '',
      content_search: urlParams.content_search !== null ? urlParams.content_search : '',
      limit: initialParams?.limit || prev.limit,
      offset: initialParams?.offset || prev.offset
    }));
  }, [initialParams, config.app.defaultLimit]);

  // Слушаем изменения URL (например, при нажатии кнопок назад/вперед)
  useEffect(() => {
    const handlePopState = () => {
      const urlParams = getUrlParams();
      setFormData(prev => ({
        ...prev,
        // Если параметр есть в URL - используем его (может быть пустой строкой для очистки)
        // Если параметра нет - очищаем поле
        author: urlParams.author !== null ? urlParams.author : '',
        content_search: urlParams.content_search !== null ? urlParams.content_search : ''
      }));
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  const [showAdvanced, setShowAdvanced] = useState(false);
  const [validationError, setValidationError] = useState('');

  const handleInputChange = (field, value) => {
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Проверяем что заполнено хотя бы одно из полей: author или content_search
    if (!formData.author.trim() && !formData.content_search.trim()) {
      setValidationError('Заполните хотя бы одно поле: Автор или Поиск по содержимому');
      return;
    }
    
    // Очищаем ошибку валидации если она была
    setValidationError('');
    
    // Очищаем пустые поля перед отправкой
    const cleanData = { ...formData };
    
    if (!cleanData.author) delete cleanData.author;
    if (!cleanData.content_search) delete cleanData.content_search;
    if (!cleanData.datetime.value) delete cleanData.datetime;
    if (!cleanData.rating_plus.value) delete cleanData.rating_plus;
    if (!cleanData.rating_minus.value) delete cleanData.rating_minus;
    if (!cleanData.haveimages) delete cleanData.haveimages;
    if (!cleanData.havevideos) delete cleanData.havevideos;
    
    onSearch(cleanData);
  };

  const clearForm = () => {
    setFormData({
      author: '',
      content_search: '',
      datetime: {
        operator: 'gte',
        value: ''
      },
      rating_plus: {
        operator: 'gte',
        value: ''
      },
      rating_minus: {
        operator: 'gte',
        value: ''
      },
      haveimages: false,
      havevideos: false,
      limit: config.app.defaultLimit,
      offset: 0
    });
    setValidationError('');
  };

  const operators = [
    { value: 'eq', label: '=' },
    { value: 'gt', label: '>' },
    { value: 'gte', label: '>=' },
    { value: 'lt', label: '<' },
    { value: 'lte', label: '<=' }
  ];

  return (
    <div className="search-form-container">
      <form ref={formRef} onSubmit={handleSubmit} className="search-form">
        <div className="form-header">
          <h2>Поиск комментариев</h2>
          <button
            type="button"
            className="toggle-advanced-btn"
            onClick={() => setShowAdvanced(!showAdvanced)}
          >
            <Filter size={20} />
            {showAdvanced ? 'Скрыть' : 'Показать'} расширенные фильтры
          </button>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="author">Автор</label>
            <input
              type="text"
              id="author"
              value={formData.author}
              onChange={(e) => handleInputChange('author', e.target.value)}
              placeholder="Введите имя автора (необязательно)"
            />
          </div>

          <div className="form-group">
            <label htmlFor="content_search">Поиск по содержимому</label>
            <input
              type="text"
              id="content_search"
              value={formData.content_search}
              onChange={(e) => handleInputChange('content_search', e.target.value)}
              placeholder="Поиск в содержимом комментария (необязательно)"
            />
          </div>
        </div>
        
        <div className="search-hint">
          <small>💡 Заполните хотя бы одно поле: Автор или Поиск по содержимому (или оба)</small>
        </div>
        
        {validationError && (
          <div className="validation-error">
            <strong>Ошибка:</strong> {validationError}
          </div>
        )}

        {showAdvanced && (
          <>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="datetime_operator">Дата/Время</label>
                <div className="operator-input-group">
                  <select
                    id="datetime_operator"
                    value={formData.datetime.operator}
                    onChange={(e) => handleInputChange('datetime.operator', e.target.value)}
                  >
                    {operators.map(op => (
                      <option key={op.value} value={op.value}>{op.label}</option>
                    ))}
                  </select>
                  <input
                    type="datetime-local"
                    value={formData.datetime.value}
                    onChange={(e) => handleInputChange('datetime.value', e.target.value)}
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="rating_plus_operator">Рейтинг +</label>
                <div className="operator-input-group">
                  <select
                    id="rating_plus_operator"
                    value={formData.rating_plus.operator}
                    onChange={(e) => handleInputChange('rating_plus.operator', e.target.value)}
                  >
                    {operators.map(op => (
                      <option key={op.value} value={op.value}>{op.label}</option>
                    ))}
                  </select>
                  <input
                    type="number"
                    value={formData.rating_plus.value}
                    onChange={(e) => handleInputChange('rating_plus.value', e.target.value)}
                    placeholder="Введите значение"
                  />
                </div>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="rating_minus_operator">Рейтинг -</label>
                <div className="operator-input-group">
                  <select
                    id="rating_minus_operator"
                    value={formData.rating_minus.operator}
                    onChange={(e) => handleInputChange('rating_minus.operator', e.target.value)}
                  >
                    {operators.map(op => (
                      <option key={op.value} value={op.value}>{op.label}</option>
                    ))}
                  </select>
                  <input
                    type="number"
                    value={formData.rating_minus.value}
                    onChange={(e) => handleInputChange('rating_minus.value', e.target.value)}
                    placeholder="Введите значение"
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="limit">Лимит результатов</label>
                <input
                  type="number"
                  id="limit"
                  value={formData.limit}
                  onChange={(e) => handleInputChange('limit', parseInt(e.target.value) || config.app.defaultLimit)}
                  min="1"
                  max={config.app.maxLimit}
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group checkbox-group">
                <label>
                  <input
                    type="checkbox"
                    checked={formData.haveimages}
                    onChange={(e) => handleInputChange('haveimages', e.target.checked)}
                  />
                  Есть изображения
                </label>
              </div>

              <div className="form-group checkbox-group">
                <label>
                  <input
                    type="checkbox"
                    checked={formData.havevideos}
                    onChange={(e) => handleInputChange('havevideos', e.target.checked)}
                  />
                  Есть видео
                </label>
              </div>
            </div>
          </>
        )}

        <div className="form-actions">
          <button ref={submitButtonRef} type="submit" className="search-btn">
            <Search size={20} />
            Поиск
          </button>
          <button type="button" onClick={clearForm} className="clear-btn">
            <X size={20} />
            Очистить
          </button>
        </div>
      </form>
    </div>
  );
});

export default SearchForm; 